import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// Setting a new password from an emailed link.
//
// Matches MIN_PASSWORD_LENGTH in backend/src/routes/auth.js. The SERVER is what
// enforces it; this only exists so somebody is told before they wait for a round
// trip. If one changes, change both.
const MIN_PASSWORD = 8;

// THE PASSWORD MANAGER RULE (house rule of 10/09/2026), AND THE ONE AWKWARD BIT.
//
// Chrome will only offer to save a new password if it can see WHICH login the
// password belongs to, which means a username field has to be on the page. On
// this screen the coach is not signed in, so the page starts out with no idea
// who they are: all it has is a token out of a link.
//
// So the token is looked up first, and the lookup hands back the address the
// link was sent to. That address is then rendered as a read-only field carrying
// autoComplete="username", exactly the pattern already working on the
// change-password form in pages/Profile.js. Off-screen rather than hidden or
// display:none, because several password managers skip fields hidden those two
// ways.
//
// It is safe to hand that address back: only somebody holding the token can ask
// for it, the token went to that very address, and it dies after one use or 60
// minutes. Whoever is asking already has the inbox.
//
// The form is a real <form> and the password fields are real type="password"
// fields with autoComplete="new-password" and stable name and id attributes.
// Nothing here carries autoComplete="off".
const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { signInWithToken } = useAuth();

  // 'checking' -> 'ready' | 'dead'
  const [stage, setStage] = useState('checking');
  const [accountEmail, setAccountEmail] = useState('');
  const [deadMessage, setDeadMessage] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  // Ask the server whether this link is still good, and what address it is for.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setStage('dead');
      setDeadMessage('This reset link is incomplete. Ask for a new one on the sign-in page and a fresh one will be sent.');
      return;
    }
    api.post('/api/auth/reset-password/lookup', { token })
      .then(res => {
        if (cancelled) return;
        setAccountEmail(res.data.email || '');
        setStage('ready');
      })
      .catch(err => {
        if (cancelled) return;
        setDeadMessage(err.response?.data?.error || 'This reset link no longer works.');
        setStage('dead');
      });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    // Caught here, in the browser, before anything is sent. The two new
    // passwords not matching is a typing mistake, and the server cannot tell
    // anyway because it only ever receives one of them.
    if (!newPassword || !confirmPassword) {
      setError('Please fill in both boxes.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The two new passwords do not match. Please type the new one again.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD) {
      setError(`Your new password must be at least ${MIN_PASSWORD} characters. Three random words is an easy way to get there.`);
      return;
    }

    if (busy) return;              // guards a double tap on a phone
    setBusy(true);
    try {
      const res = await api.post('/api/auth/reset-password', { token, newPassword });
      // Signed in on this device, which is what the message says. Put the token
      // and the user away BEFORE navigating, or the next request goes out
      // without one and bounces straight back to the login screen.
      signInWithToken(res.data.token, res.data.user);
      setSaved('Password saved. You are signed in.');
      // A moment on the confirmation, then into the portal. The delay is also
      // what gives Chrome the chance to offer to save the password: the fields
      // are still in the DOM when the submit succeeds.
      setTimeout(() => navigate('/', { replace: true }), 1200);
    } catch (err) {
      // A 410 here means the link died between the lookup and the save — used on
      // another device, or an hour went by while the form was open.
      if (err.response?.status === 410) {
        setDeadMessage(err.response?.data?.error || 'This reset link no longer works.');
        setStage('dead');
        return;
      }
      setError(err.response?.data?.error || 'Your password could not be saved. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>City of Preston Basketball</h1>
        <p className="muted">Coach portal</p>

        {stage === 'checking' && <p className="muted" style={{ textAlign: 'center' }}>Checking your link…</p>}

        {stage === 'dead' && (
          <>
            <div className="error-msg prewrap">{deadMessage}</div>
            <p className="muted" style={{ textAlign: 'center', marginBottom: 0 }}>
              <Link to="/forgot-password">Ask for a new link</Link>
              {' · '}
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        )}

        {stage === 'ready' && (
          <>
            <h2>Set a new password</h2>
            {error && <div className="error-msg">{error}</div>}
            {saved && <div className="success-msg">{saved}</div>}

            <p className="muted" style={{ textAlign: 'left', marginTop: 0 }}>
              Choose a new password for your City of Preston Basketball coach portal account.
            </p>

            <form onSubmit={submit} className="password-form">
              {/* See the long note at the top of this file. This is what lets
                  Chrome pair the new password with the right saved login.
                  Required by the house rule of 10/09/2026. */}
              <input
                type="text"
                name="username"
                id="reset-username"
                autoComplete="username"
                value={accountEmail}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                className="offscreen-username"
              />

              <div className="form-row">
                <label htmlFor="reset-new-password">New password</label>
                <input
                  id="reset-new-password"
                  name="newPassword"
                  type={showPasswords ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); setError(''); }}
                  autoFocus
                />
              </div>

              <div className="form-row">
                <label htmlFor="reset-confirm-password">Type the new password again</label>
                <input
                  id="reset-confirm-password"
                  name="confirmPassword"
                  type={showPasswords ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                />
              </div>

              {/* Worth having on a phone in a sports hall, where a mistyped
                  password you cannot see is the usual reason this form fails. */}
              <label className="muted password-show">
                <input
                  type="checkbox"
                  checked={showPasswords}
                  onChange={e => setShowPasswords(e.target.checked)}
                />
                Show what I am typing
              </label>

              <button className="btn" type="submit" disabled={busy || !!saved} style={{ width: '100%' }}>
                {busy ? 'Saving…' : 'Save new password'}
              </button>
            </form>

            <p className="muted" style={{ textAlign: 'left' }}>
              At least {MIN_PASSWORD} characters. Three random words you will remember beats something
              short and complicated. There are no other rules.
            </p>
            <p className="muted" style={{ textAlign: 'left', marginBottom: 0 }}>
              Saving a new password signs you out everywhere else you are signed in.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
